import React, { useState } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkle, Calculator, Trophy } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import HelpHint from '@/components/HelpHint';
import useTrackToolUsage from '@/hooks/useTrackToolUsage';

const SocialSecurity = () => {
  useTrackToolUsage('social_security');
  const [inputs, setInputs] = useState({
    primary_pia: 2500,
    spouse_pia: 1800,
    primary_age: 60,
    spouse_age: 60,
    primary_life_exp: 85,
    spouse_life_exp: 87,
    other_income: 30000,
    filing_status: 'married_filing_jointly'
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAnalyze = async () => {
    setLoading(true);
    try {
      const response = await api.post('/analyzers/social-security/optimize', inputs);
      setResult(response.data);
      toast.success('Optimization complete');
    } catch (error) {
      toast.error('Optimization failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 fade-in">
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Educational Optimization Engine:</strong> Compares Social Security claiming strategies using simplified rules. Your PIA estimate is at <strong>ssa.gov/myaccount</strong>. Actual benefits depend on earnings history. Consult a Social Security specialist for personalized planning.
          </p>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-3">
          <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
            Social Security Optimization Engine
          </h1>
          <HelpHint
            topic="How Social Security Optimization works"
            query="social security claiming break even spousal"
            testid="ss-help-hint"
          />
        </div>
        <p className="text-base text-[#4A5568]">
          Break-even analysis, spousal benefits, and claiming strategies
        </p>
      </div>

      {/* Inputs */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="ss-inputs-card">
        <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Your Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="primary_pia">Your Monthly PIA at FRA ($)</Label>
            <Input id="primary_pia" type="number" data-testid="ss-primary-pia-input" value={inputs.primary_pia}
              onChange={(e) => setInputs({ ...inputs, primary_pia: Number(e.target.value) })} className="border-slate-300" />
          </div>
          <div>
            <Label htmlFor="spouse_pia">Spouse Monthly PIA ($, 0 if single)</Label>
            <Input id="spouse_pia" type="number" data-testid="ss-spouse-pia-input" value={inputs.spouse_pia}
              onChange={(e) => setInputs({ ...inputs, spouse_pia: Number(e.target.value) })} className="border-slate-300" />
          </div>
          <div>
            <Label htmlFor="primary_age">Your Current Age</Label>
            <Input id="primary_age" type="number" data-testid="ss-primary-age-input" value={inputs.primary_age}
              onChange={(e) => setInputs({ ...inputs, primary_age: Number(e.target.value) })} className="border-slate-300" />
          </div>
          <div>
            <Label htmlFor="spouse_age">Spouse Current Age</Label>
            <Input id="spouse_age" type="number" data-testid="ss-spouse-age-input" value={inputs.spouse_age}
              onChange={(e) => setInputs({ ...inputs, spouse_age: Number(e.target.value) })} className="border-slate-300" />
          </div>
          <div>
            <Label htmlFor="primary_life_exp">Your Life Expectancy</Label>
            <Input id="primary_life_exp" type="number" data-testid="ss-primary-life-input" value={inputs.primary_life_exp}
              onChange={(e) => setInputs({ ...inputs, primary_life_exp: Number(e.target.value) })} className="border-slate-300" />
          </div>
          <div>
            <Label htmlFor="spouse_life_exp">Spouse Life Expectancy</Label>
            <Input id="spouse_life_exp" type="number" data-testid="ss-spouse-life-input" value={inputs.spouse_life_exp}
              onChange={(e) => setInputs({ ...inputs, spouse_life_exp: Number(e.target.value) })} className="border-slate-300" />
          </div>
          <div>
            <Label htmlFor="other_income">Other Annual Income ($)</Label>
            <Input id="other_income" type="number" data-testid="ss-other-income-input" value={inputs.other_income}
              onChange={(e) => setInputs({ ...inputs, other_income: Number(e.target.value) })} className="border-slate-300" />
          </div>
          <div>
            <Label htmlFor="filing_status">Filing Status</Label>
            <Select value={inputs.filing_status} onValueChange={(v) => setInputs({ ...inputs, filing_status: v })}>
              <SelectTrigger data-testid="ss-filing-select" className="border-slate-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Single</SelectItem>
                <SelectItem value="head_of_household">Head of Household</SelectItem>
                <SelectItem value="married_filing_jointly">Married Filing Jointly</SelectItem>
                <SelectItem value="married_filing_separately">Married Filing Separately</SelectItem>
                <SelectItem value="qualifying_widow">Qualifying Widow(er)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={handleAnalyze} disabled={loading} data-testid="ss-analyze-button" className="mt-6 bg-[#0A1128] hover:bg-[#152145]">
          <Calculator size={18} className="mr-2" />
          {loading ? 'Optimizing...' : 'Run Optimization'}
        </Button>
      </div>

      {result && (
        <>
          {/* Recommended */}
          {result.recommended && (
            <div className="bg-[#0A1128] text-white rounded-md p-6 dashboard-card" data-testid="ss-recommendation">
              <div className="flex items-center gap-3 mb-2">
                <Trophy size={24} weight="duotone" className="text-[#C5A059]" />
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#A0AABF]">Highest Lifetime Total</p>
              </div>
              <h2 className="cabinet-grotesk text-2xl font-medium text-[#C5A059]">{result.recommended}</h2>
              <p className="text-sm text-[#A0AABF] mt-2">Based on your inputs, this strategy produces the highest lifetime benefits across both you and your spouse.</p>
            </div>
          )}

          {/* Break-even */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-testid="ss-breakeven-cards">
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5 dashboard-card">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Annual at Age 62</p>
              <p className="text-2xl font-semibold text-[#4A5568] ibm-plex">${result.breakeven_analysis.annual_at_62.toLocaleString()}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5 dashboard-card">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Annual at Age 70</p>
              <p className="text-2xl font-semibold text-[#C5A059] ibm-plex">${result.breakeven_analysis.annual_at_70.toLocaleString()}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5 dashboard-card">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Break-Even Age</p>
              <p className="text-2xl font-semibold text-[#2D6A4F] ibm-plex">{result.breakeven_analysis.breakeven_age}</p>
              <p className="text-xs text-[#4A5568] mt-2">{result.breakeven_analysis.note}</p>
            </div>
          </div>

          {/* Strategy Comparison Chart */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="ss-strategy-chart">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Lifetime Benefits Comparison</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={result.strategies}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#4A5568" style={{ fontSize: '11px' }} angle={-15} textAnchor="end" height={80} />
                <YAxis stroke="#4A5568" />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Bar dataKey="lifetime_total" fill="#C5A059" name="Lifetime Total Benefits" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Strategy Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {result.strategies.map((s, idx) => (
              <div key={idx} className={`border rounded-md p-5 dashboard-card ${idx === 0 ? 'border-[#C5A059] bg-[#C5A059]/5' : 'border-slate-200 bg-white'}`} data-testid={`ss-strategy-${idx}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="cabinet-grotesk text-lg font-medium text-[#0A1128]">{s.name}</h3>
                    <p className="text-xs text-[#4A5568] mt-1">{s.description}</p>
                  </div>
                  {idx === 0 && (
                    <span className="px-2 py-1 bg-[#C5A059] text-[#0A1128] rounded text-xs font-semibold uppercase tracking-[0.08em]">Best</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <p className="text-xs text-[#4A5568]">You Claim @ {s.primary_claim_age}</p>
                    <p className="text-lg font-semibold text-[#0A1128] ibm-plex">${s.primary_monthly.toLocaleString()}/mo</p>
                  </div>
                  {s.spouse_claim_age && (
                    <div>
                      <p className="text-xs text-[#4A5568]">Spouse Claims @ {s.spouse_claim_age}</p>
                      <p className="text-lg font-semibold text-[#0A1128] ibm-plex">${s.spouse_monthly.toLocaleString()}/mo</p>
                    </div>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-xs text-[#4A5568]">Lifetime Total</p>
                  <p className="text-2xl font-semibold text-[#C5A059] ibm-plex">${s.lifetime_total.toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Taxability */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="ss-taxability">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Taxability Analysis</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-1">Annual SS Income</p>
                <p className="text-xl font-semibold text-[#0A1128] ibm-plex">${result.taxability.annual_ss_income.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-1">Combined Income</p>
                <p className="text-xl font-semibold text-[#0A1128] ibm-plex">${result.taxability.combined_income.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-1">Taxable Portion</p>
                <p className="text-xl font-semibold text-[#C53030] ibm-plex">{result.taxability.taxable_percentage}%</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-1">Estimated Taxable $</p>
                <p className="text-xl font-semibold text-[#C53030] ibm-plex">${result.taxability.estimated_taxable_amount.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SocialSecurity;
